<template>
  <template v-if="auditorType === 'image'">
    <v-layout :full-height="true" style="z-index: 9;" class="flex-column">
      <div
        ref="modalDisplay"
        class="display"
        :tabindex="selectedAnnotations.length > 0"
      >
        <BaseSidebar :collapsible="true">
          <div class="annotation-editor-modal__sidebar">
            <JsonRawViewer
              v-model:displayLabelTags="displayLabelTags"
              v-model:selectedAnnotationSets="annotationDisplaySettings.displayAnnotationSets.annotation_sets"
              v-model:selectedLabels="annotationDisplaySettings.displayLabels.labels"
              v-model:selectedLabel="selectedLabel"
              v-model:displayProjection="displayProjection"
              v-model:colorAnnotationsBy="annotationDisplaySettings.colorAnnotationsBy"
              v-model:showTypes="showTypes"
              :labels="labels"
              :imageObj="currentSample.imageObj"
              :annotationSets="annotationSets"
              :fixedAnnotationSet="Boolean(annotationSetToEdit)"
              :groupedAnnotations="groupedAnnotations"
              :selectedAnnotationIdentifiers="selectedAnnotationIdentifiers"
              :isEditing="mode === 'edit'"
              :hideAddTopics="mode !== 'edit'"
              @remove-annotation="handleAnnotationDeleteClicked"
              @select-annotations="selectAnnotation"
              @edit-for-annotation-set="handleStartEditingAnnotations"
              @new-color="(params)=>updateLabelColor(params, { dataset_id: currentSample?.imageObj?.dataset_id })"
              @add-annotation-sets="openAddAnnotationSetsModal"
              @add-labels="openAddLabelsModal"
            >
              <template #utilities-slot>
                <AddVisualizationMenu @layout-selected="handleAddVisualization" />
                <SelectLayoutMenu @layout-selected="handleLayoutSelected" />
              </template>
            </JsonRawViewer>
            <template v-if="currentSample.imageObj && groups.length > 0">
              <div class="image-group">
                <label class="image-group__label">Group</label>
                <ImageGroupSelector :imageGroupId="imageGroupId" :groups="groups" @change="handleImageGroupChanged" />
              </div>
            </template>
          </div>
        </BaseSidebar>

        <div class="visualization">
          <template v-if="mode === 'edit'">
            <AnnotationToolAGTGRibbon
              v-if="drawMode === 'sam2'"
              ref="annotationToolAGTGRibbon"
              @submit="handleSequenceSubmit"
            />
            <AnnotationToolSequenceRibbon
              v-else-if="internalEditAsSequence || isSequence"
              ref="annotationToolSequenceRibbon"
              :canUndo="prevInternalAnnotations !== null"
              :hasSelection="selectedAnnotationIdentifiers.length > 0"
              :annotationDisplaySettings="annotationDisplaySettings"
              :isAuditMode="isAuditMode"
              :frameImageObj="currentSample.imageObj"
              :reviewSettings="reviewSettings"
              :selectedAnnotations="selectedAnnotations"
              :hasCopiedAnnotations="hasCopiedAnnotations"
              @undo="handleUndo"
              @revert-changes="handleRevertChanges"
              @show-annotation-info="showAnnotationInfo = true"
              @submit="handleSequenceSubmit"
              @copy="handleCopyAnnotations"
              @paste="handlePasteAnnotations"
              @bulk-del-ann="handleDeleteAnnotationBulk"
            />
            <AnnotationToolRibbon
              v-else
              :canUndo="prevInternalAnnotations !== null"
              :hasSelection="selectedAnnotationIdentifiers.length > 0"
              :annotationDisplaySettings="annotationDisplaySettings"
              :isAuditMode="isAuditMode"
              @undo="handleUndo"
              @revert-changes="handleRevertChanges"
              @show-annotation-info="showAnnotationInfo = true"
              @submit="handleSubmit"
              @bulk-del-ann="handleDeleteAnnotationBulk"
            />
          </template>
          <VisualizationDisplays
            ref="visualizationDisplays"
            v-model:layout="layout"
            :loading="isInitializingAnimation || ((internalEditAsSequence || isSequence) && isLoadingSample)"
            @drop="handleVisualizationContainerDrop"
            @visualization-selected="handleVisualizationSelected"
          />
          <div v-if="internalEditAsSequence && isSequence" class="visualization__horizontal-divider" />
          <AnimationControls
            v-if="isMounted && (internalEditAsSequence || sequenceId)"
            ref="animationControls"
            :sequenceId="sequenceId"
            :datasetId="datasetId"
            :reviewSettings="reviewSettings"
            :labels="labels"
            :selectedAnnotationSets="annotationDisplaySettings.displayAnnotationSets.annotation_sets"
            @new-frame="sam2SidebarNewFrameHandler"
          />
        </div>

        <BaseSidebar v-if="drawMode === 'sam2'" :collapsible="false">
          <div class="annotation-editor-modal__sidebar">
            <SAM2Sidebar ref="sam2Sidebar" @mode="(params) => drawMode=params" @new-frame="handleUpdateCacheForNewFrame" />
          </div>
        </BaseSidebar>

        <BaseSidebar
          :title="'Annotation Info'"
          :closeable="true"
          :collapsible="false"
          :placement="'right'"
          :show="showAnnotationInfo"
          @closed="showAnnotationInfo = false"
        >
          <AnnotationDetails :annotationObj="selectedAnnotationInfo" />
        </BaseSidebar>
        <BaseSidebar
          v-if="isAuditMode && reviewSettings && submitType === 'submit-accepted-annotations'"
          class="annotation-review-sidebar"
          :title="'Annotation Review'"
          :closeable="false"
          :collapsible="false"
          :placement="'right'"
          :show="true"
        >
          <div class="annotation-review-sidebar__buttons">
            <button
              class="button button-sm button-secondary"
              :title="'Accept All Annotations'"
              @click="acceptAllAnnotations"
            >
              Accept All Annotations
            </button>
          </div>
          <div class="annotation-review-sidebar__annotations scrollbar">
            <div
              v-for="(annotationObj, index) in currentSample.annotations"
              :key="index"
              class="annotation-review-sidebar__snap-container"
            >
              <GalleryGridAnnotationReviewItem
                v-model:reviewStatus="annotationObj.reviewStatus"
                :imageObj="currentSample.imageObj"
                :annotationSets="annotationSets"
                :labels="labels"
                :annotation="annotationObj"
                @edit-clicked="handleAnnotationReviewItemEdit"
              />
            </div>
          </div>
        </BaseSidebar>
      </div>
      <AnnotationEditorHistoryDrawer
        v-if="hasReviewTask && reviewSettings !== null && reviewSettings !== undefined"
        :active="true"
        :images="doneImages"
        :currentImage="currentTaskImage"
        :pendingImages="pendingTaskImages"
        :activeIndex="activeImageIndex"
        :component="'footer'"
        :reviewSettings="reviewSettings"
        :sequences="sequences"
        @image-clicked="handleTaskImageClicked"
        @update:selected-sequence="handleSelectedSequenceChanged"
      />
    </v-layout>
  </template>
  <template v-else-if="auditorType === 'annotation'">
    <AnnotationReview
      v-if="hasSourceAnnotationSet && reviewSettings"
      ref="annotationReview"
      :reviewSettings="reviewSettings"
      :activeImageIndex="activeImageIndex"
      :currentTaskImage="currentTaskImage"
      :pendingImages="pendingTaskImages"
      :annotations="currentSample.annotations"
      :labels="labels"
      @image-clicked="handleTaskImageClicked"
    />
  </template>

  <template v-if="showLayouts">
    <VisualizationPane
      v-if="visualizationLocations?.canvas"
      :paneType="'canvas'"
      :to="getLocationId(visualizationLocations.canvas)"
      @pane-clicked="handlePaneClick"
    >
      <AnnotationToolViewingModeControls
        :hasImage="currentSample.imageObj && 'id' in currentSample.imageObj"
        :canvasScale="canvasScale"
        :hasSelection="selectedAnnotationIdentifiers.length > 0"
        @zoom-to-fit="handleZoomToFit"
        @zoom-to-selection="handleZoomToSelection"
        @download-image="downloadImage"
      />
      <DrawingToolModeControls
        v-if="mode === 'edit'"
        ref="drawingToolModeControls"
        v-model:mode="drawMode"
        v-model:size="drawCursorSize"
        :shouldEnableEraser="selectedAnnotationIdentifiers.length === 1"
        :imageDimensions="imageDimensions"
        :isSequence="isSequence"
        :hasLabels="labels && labels.length > 0"
      />
      <div
        ref="annotationTool"
        class="annotation-tool"
      >
        <template v-if="mode === 'edit'">
          <AnnotationCanvas
            v-if="currentSample.imageObj"
            ref="annotationCanvas"
            v-model:updatedAnnotations="currentSample.annotations"
            v-model:scale="canvasScale"
            v-model:selectedAnnotationIdentifiers="selectedAnnotationIdentifiers"
            :selectedAnnotationSets="annotationDisplaySettings.displayAnnotationSets.annotation_sets"
            :selectedLabels="annotationDisplaySettings.displayLabels.labels"
            :displayLabelTags="displayLabelTags"
            :annotations="currentSample.annotations"
            :annotationSets="annotationSets"
            :imageObj="currentSample.imageObj"
            :labels="labels"
            :selectedLabel="selectedLabel"
            :colorBy="colorBy"
            :showAnnotations="showAnnotations"
            :editingEnabled="true"
            :useImageCacheOnly="(internalEditAsSequence || isSequence) ? true : false"
            :imageCacheElem="currentSample?.image"
            :highlightedAnnotationIdentifier="hoveredAnnotationListIdentifier"
            :isAuditMode="isAuditMode"
            :displayProjection="displayProjection"
            :showTypes="showTypes"
            :drawMode="drawMode"
            :drawCursorSize="drawCursorSize"
            :doneVertexDraw="doneVertexDraw"
            :lidarProjections="currentSample?.lidarProjections"
            :pcdProjections="currentSample?.pcdProjections"
            :pendingAnnotations="pendingAnnotations"
            @create-annotation="createNewInternalAnnotation"
            @image-loaded="handleImageLoaded"
            @remove-annotation="handleAnnotationDeleteClicked"
          />
        </template>
        <template v-else-if="mode === 'view'">
          <AnnotationCanvas
            v-if="currentSample.imageObj"
            ref="annotationCanvas"
            v-model:scale="canvasScale"
            v-model:selectedAnnotationIdentifiers="selectedAnnotationIdentifiers"
            :selectedAnnotationSets="annotationDisplaySettings.displayAnnotationSets.annotation_sets"
            :selectedLabels="annotationDisplaySettings.displayLabels.labels"
            :annotations="currentSample.annotations"
            :annotationSets="annotationSets"
            :imageObj="currentSample.imageObj"
            :labels="labels"
            :colorBy="colorBy"
            :showAnnotations="showAnnotations"
            :displayLabelTags="displayLabelTags"
            :editingEnabled="false"
            :useImageCacheOnly="true"
            :imageCacheElem="currentSample?.image"
            :displayProjection="displayProjection"
            :showTypes="showTypes"
            :showPointerAxes="false"
            :lidarProjections="currentSample?.lidarProjections"
            :pcdProjections="currentSample?.pcdProjections"
          />
        </template>
        <div v-else-if="isMounted && !internalEditAsSequence" id="no-image-remaining">No Images Remaining</div>
      </div>
      <VideoControls v-if="isVideo" />
    </VisualizationPane>

    <VisualizationPane
      v-if="visualizationLocations?.['3d-canvas']"
      :paneType="'3d-canvas'"
      :to="getLocationId(visualizationLocations['3d-canvas'])"
      @pane-clicked="handlePaneClick"
    >
      <PointCloudViewer
        v-model:updatedAnnotations="currentSample.annotations"
        v-model:selectedAnnotationIdentifiers="selectedAnnotationIdentifiers"
        :imageObj="currentSample.imageObj"
        :annotations="currentSample.annotations"
        :pendingAnnotations="aigt3dAnnotations"
        :labels="labels"
        :selectedLabel="selectedLabel"
        :selectedLabels="annotationDisplaySettings.displayLabels.labels"
        :selectedAnnotationSets="annotationDisplaySettings.displayAnnotationSets.annotation_sets"
        :isAuditMode="isAuditMode"
        :currentTool="current3DCanvasTool"
        :showTypes="showTypes"
        :points="currentSample?.pcd"
        :lidar="currentSample?.lidar"
        :editingEnabled="mode === 'edit'"
        @create-annotation="createNewInternalAnnotation"
      />
      <DrawingToolModeControls3d
        v-if="mode === 'edit'"
        v-model:current3DCanvasTool="current3DCanvasTool"
        :hasLabels="labels && labels.length > 0"
      />
    </VisualizationPane>

    <VisualizationPane
      v-if="visualizationLocations?.['depth-map']"
      :paneType="'depth-map'"
      :to="getLocationId(visualizationLocations['depth-map'])"
      @pane-clicked="handlePaneClick"
    >
      <DepthMapViewer
        :showDepthMap="showTypes?.depthmap"
      />
    </VisualizationPane>
  </template>
  <BulkDeleteAnnotationModal
    ref="bulkDeleteModal"
    :frameCount="frameCount"
    :imageObj="currentSample?.imageObj"
    :annotationSets="annotationSets"
    @deleted="handleAnnotationBulkDeleteComplete"
  />
  <CreateAnnotationSetModal
    ref="createAnnotationSetModal"
    :datasetId="sourceDatasetId"
    @annotation-set-created="newAnnotationSetCreated"
  />
  <CreateLabelsModal
    ref="createLabelsModal"
    :datasetId="sourceDatasetId"
    @label-created="newLabelsCreated"
  />
</template>

<script setup>
import BaseSidebar from '@/components/BaseSidebar.vue';
import AnnotationCanvas from '@/components/DatasetComponent/AnnotationTool/AnnotationCanvas.vue';
import AnnotationToolRibbon from '@/components/DatasetComponent/AnnotationTool/AnnotationToolRibbon.vue';
import AnnotationToolSequenceRibbon from '@/components/DatasetComponent/AnnotationTool/AnnotationToolSequenceRibbon.vue';
import AnnotationToolAGTGRibbon from '@/components/DatasetComponent/AnnotationTool/AnnotationToolAGTGRibbon.vue';
import AnnotationDetails from '@/components/DatasetComponent/AnnotationTool/AnnotationDetails.vue';
import GalleryGridAnnotationReviewItem from '@/components/DatasetComponent/GalleryComponent/GalleryGridAnnotationReviewItem.vue';
import AnnotationEditorHistoryDrawer from '@/components/DatasetComponent/AnnotationTool/AnnotationEditorHistoryDrawer.vue';
import AnnotationReview from '@/components/DatasetComponent/AnnotationTool/AnnotationReview.vue';
import ImageGroupSelector from '@/components/ImageGroupSelector.vue';
import {
  ref, toRefs, watch, computed, onMounted, onUnmounted, nextTick,
} from 'vue';
import useTasks from '@/composables/annotationTool/useTasks.js';
import { useEditorTaskStore } from '@/stores/useEditorTaskStore.js';
import useAnnotationEditorModalHotkeys from '@/composables/annotationTool/useAnnotationEditorModalHotkeys.js';
import VideoControls from '@/components/DatasetComponent/AnnotationTool/VideoControls/VideoControls.vue';
import { useViewerVisualizationsStore } from '@/stores/useViewerVisualizationsStore.js';
import { useEditorControlsStore } from '@/stores/useEditorControlsStore.js';
import { useEditorStore } from '@/stores/useEditorStore.js';
import { storeToRefs } from 'pinia';
import PointCloudViewer from '@/components/DatasetComponent/AnnotationTool/PointCloudViewer.vue';
import JsonRawViewer from '@/components/DatasetComponent/AnnotationTool/JsonRawViewer.vue';
import { useStore } from 'vuex';
import AnnotationToolViewingModeControls from '@/components/DatasetComponent/AnnotationTool/AnnotationToolViewingModeControls.vue';
import BulkDeleteAnnotationModal from '@/components/DatasetComponent/AnnotationTool/BulkDeleteAnnotationModal.vue';
import DepthMapViewer from '@/components/DatasetComponent/AnnotationTool/DepthMapViewer.vue';
import DrawingToolModeControls from '@/components/DatasetComponent/AnnotationTool/DrawingToolModeControls.vue';
import DrawingToolModeControls3d from '@/components/DatasetComponent/AnnotationTool/DrawingToolModeControls3d.vue';
import VisualizationPane from '@/components/DatasetComponent/AnnotationTool/VisualizationPane.vue';
import VisualizationDisplays from '@/components/DatasetComponent/AnnotationTool/VisualizationDisplays.vue';
import SelectLayoutMenu from '@/components/DatasetComponent/AnnotationTool/SelectLayoutMenu.vue';
import AddVisualizationMenu from '@/components/DatasetComponent/AnnotationTool/AddVisualizationMenu.vue';
import useVisualizationDisplays from '@/composables/annotationTool/useVisualizationDisplays.js';
import AnimationControls from '@/components/DatasetComponent/AnnotationTool/AnimationControlsV2.vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import { useSAM2Store } from '@/stores/useSAM2Store.js';
import SAM2Sidebar from '@/components/DatasetComponent/AnnotationTool/SAM2Sidebar.vue';
import useAnnotationType from '@/composables/useAnnotationType.js';
import { useRoute, useRouter } from 'vue-router';
import useAnnotationColorMap from '@/composables/useAnnotationColorMap.js';
import useGallery from '@/composables/annotationTool/useGallery.js';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';
import CreateLabelsModal from '@/components/DatasetComponent/DatasetManagement/CreateLabelsModal.vue';
import CreateAnnotationSetModal from '@/components/DatasetComponent/DatasetManagement/CreateAnnotationSetModal.vue';

const props = defineProps({
  initialAnnotationDisplaySettings: {
    type: Object,
    default: () => (
      {
        displayAnnotationSets: { annotation_sets: [] },
        displayLabels: { labels: [] },
        colorAnnotationsBy: { type: "label" },
      }
    ),
  },
});
const {
  initialAnnotationDisplaySettings,
} = toRefs(props);

const store = useStore();
const route = useRoute();
const router = useRouter();
const $toast = useToast();

const canEdit = computed(() => {
  if (!store.state.user.user) {
    return false;
  }
  // TODO: add canEdit based on user permissions
  return true;
});

const {
  datasetFilters,
  setDatasetFilters,
  annotationDisplaySettings,
} = useGallery();
if (!annotationDisplaySettings.value) {
  annotationDisplaySettings.value = {
    displayAnnotationSets: { annotation_sets: initialAnnotationDisplaySettings.value.displayAnnotationSets.annotation_sets },
    displayLabels: { labels: initialAnnotationDisplaySettings.value.displayLabels.labels },
    colorAnnotationsBy: { type: initialAnnotationDisplaySettings.value.colorAnnotationsBy.type },
  };
}

const editorStore = useEditorStore();
const {
  mode,
  currentSample,
  prevInternalAnnotations,
  sequences,
  imageGroupId,
  datasetId,
  sequenceId,
  isSequence,
  isVideo,
  labels,
  annotationSets,
  annotationSetToEdit,
  imageDimensions,
  canvasScale,
  selectedAnnotationIdentifiers,
  annotationIdentifierMap,
  selectedAnnotations,
  selectedAnnotationInfo,
  hasCopiedAnnotations,
} = storeToRefs(editorStore);
const {
  $reset: resetEditorStore,
  handleImageLoaded,
  selectAnnotation,
  handleUndo,
  handleRevertChanges,
  handleCopyAnnotations,
  handlePasteAnnotations,
  copyAnnotationsObject,
  setCurrentAnnotations,
  addToCurrentAnnotations,
} = editorStore;
onUnmounted(() => {
  resetEditorStore();
  editorStore.$dispose();
});
const labelColorMap = ref({});

const {
  jsonViewerShowTypes: showTypes,
} = useAnnotationType();

const annotationCanvas = ref(null);
// const annotationSetToEdit = ref(null);
const bulkDeleteModal = ref(null);
const createAnnotationSetModal = ref(null);
const createLabelsModal = ref(null);
const annotationToolSequenceRibbon = ref(null);
const annotationToolAGTGRibbon = ref(null);
const drawingToolModeControls = ref(null);
const sam2Sidebar = ref(null);

const displayLabelTags = ref({
  box: true,
});
const displayProjection = ref({
  '3dbox': false,
  lidar: false,
  pcd: false,
});
const showAnnotations = ref(true);
const showAnnotationInfo = ref(false);
const hoveredAnnotationListIdentifier = ref(null);
const activePane = ref(null);
const isMounted = ref(false);
const internalEditAsSequence = ref(false);

// SAM2
const sam2Store = useSAM2Store();
const { $reset: resetSAM2 } = sam2Store;
const {
  isPropagating,
  sam2Server,
  isSam2ServerAvailable,
  sam2Annotations,
  aigt3dAnnotations,
  aigt2dAnnotations,
} = storeToRefs(sam2Store);
onMounted(() => {
  resetSAM2();
});
onUnmounted(() => {
  resetSAM2();
});

function sam2SidebarNewFrameHandler() {
  if (sam2Server.value && isSam2ServerAvailable.value) {
    sam2Sidebar.value.handleNewFrame();
  }
}

async function handleUpdateCacheForNewFrame() {
  await animationControls.value.cacheImageForNewFrame();
}

// Setup Tasks
const {
  reviewSettings,
  currentTaskImage,
  pendingTaskImages,
  sourceDatasetId,
  sourceAnnotationSet,
  hasSourceAnnotationSet,
  destinationAnnotationSet,
  isAuditMode,
  groups,
  auditorType,
  hasReviewTask,
  setupTaskImages,
  isFetchingNewImage,
  handleLabellingSubmitAnnotations,
  acceptAllAnnotations,
  activeImageIndex,
  doneImages,
  switchToTaskImage,
  submitType,
  handleAuditSubmitAllAnnotations,
  handleAuditSubmitAcceptedAnnotations,
  handleFrameAuditSubmitAllAnnotations,
  handleFrameAuditSubmitAcceptedAnnotations,
  skipTaskImage,
  getTaskSequences,
  doneImageOpen,
} = useTasks();
const editorTaskStore = useEditorTaskStore();
const {
  $reset: resetTaskStore,
} = editorTaskStore;
onUnmounted(() => {
  resetTaskStore();
  editorTaskStore.$dispose();
});
if (reviewSettings.value?.initialAuditorType) {
  auditorType.value = reviewSettings.value?.initialAuditorType;
} else if (route.query.auditorType) {
  auditorType.value = route.query.auditorType;
}
watch(auditorType, async (old, newval) => {
  isFetchingNewImage.value = true;

  await setupTaskImages();

  isFetchingNewImage.value = false;
});

// Controls
const editorControlsStore = useEditorControlsStore();
const {
  selectedLabel, drawMode, current3DCanvasTool, drawCursorSize, doneVertexDraw,
} = storeToRefs(editorControlsStore);
onUnmounted(() => {
  const { $reset } = useEditorControlsStore();
  $reset();
});

// Create hotkeys
const viewHotkeys = {
  'z': () => {
    handleZKey();
  },
};
const canvasHotkeys = {
  'Escape': () => {
    if (activePane.value === 'canvas') {
      selectedAnnotationIdentifiers.value = [];
      drawMode.value = 'pointer';
    } else if (activePane.value === '3d-canvas') {
      selectedAnnotationIdentifiers.value = [];
      selectedLabel.value = labels.value[0];
      current3DCanvasTool.value = '3d-pointer';
    }
  },
};
const canvas3DHotkeys = {
  'q': () => { current3DCanvasTool.value = '3d-pointer'; },
  'b': () => { current3DCanvasTool.value = '3d-box'; },
  'l': () => { current3DCanvasTool.value = '3d-edit-label'; },
  'g': () => { current3DCanvasTool.value = 'translate-object'; },
  's': () => { current3DCanvasTool.value = 'scale-object'; },
  'Escape': async () => {
    selectedAnnotationIdentifiers.value = [];
    selectedLabel.value = labels.value[0];
    current3DCanvasTool.value = '3d-pointer';
  },
};
const toolHotkeys = ref({
  ...canvasHotkeys,
});
const auditHotkeys = ref({
  'v': () => {
    selectedAnnotations.value.forEach((anno) => {
      anno.reviewStatus = 'verified';
    });
  },
  'V': () => {
    selectedAnnotations.value.forEach((anno) => {
      anno.reviewStatus = 'unreviewed';
    });
  },
  ' ': () => {
    selectedAnnotations.value.forEach((anno) => {
      if (anno.reviewStatus === 'verified') {
        anno.reviewStatus = 'unreviewed';
      } else {
        anno.reviewStatus = 'verified';
      }
    });
  },
  'Enter': async () => {
    if (submitType.value === 'submit-all-annotations') {
      if (internalEditAsSequence.value || isSequence.value) {
        handleFrameAuditSubmitAllAnnotations(currentSample.value.imageObj);
        currentSample.value.imageObj.annotations = currentSample.value.annotations;
        handleSequenceSubmit();
      } else {
        handleAuditSubmitAllAnnotations();
      }
    } else if (submitType.value === 'submit-accepted-annotations') {
      if (internalEditAsSequence.value || isSequence.value) {
        handleFrameAuditSubmitAcceptedAnnotations(currentSample.value.imageObj);
        currentSample.value.imageObj.annotations = currentSample.value.annotations;
        handleSequenceSubmit();
      } else {
        handleAuditSubmitAcceptedAnnotations();
      }
    }
  },
});
const drawToolHotKeys = ref({
  'q': () => {
    if (activePane.value === 'canvas') {
      drawMode.value = 'pointer';
    } else if (activePane.value === '3d-canvas') {
      current3DCanvasTool.value = '3d-pointer';
    }
  },
  'b': () => {
    if (activePane.value === 'canvas') {
      drawMode.value = "box";
    } else if (activePane.value === '3d-canvas') {
      current3DCanvasTool.value = '3d-box';
    }
  },
  'a': () => {
    if (activePane.value === 'canvas') {
      drawingToolModeControls.value.confirmLaunchAGTGServer();
    }
  },
  'w': () => {
    if (activePane.value === 'canvas') {
      drawMode.value = "brush";
    }
  },
  'l': () => {
    if (activePane.value === 'canvas') {
      drawMode.value = 'color';
    } else if (activePane.value === '3d-canvas') {
      current3DCanvasTool.value = '3d-edit-label';
    }
  },
  'p': () => {
    if (activePane.value === 'canvas') {
      drawMode.value = 'line';
    }
  },
  'e': () => {
    if (activePane.value === 'canvas') {
      if (selectedAnnotationIdentifiers.value.length === 1) {
        drawMode.value = "eraser";
      }
    }
  },
  '=': () => {
    if (activePane.value === 'canvas') {
      if (drawCursorSize.value < (0.2 * imageDimensions.value.width)) {
        drawCursorSize.value *= 1.3;
      }
    }
  },
  '-': () => {
    if (activePane.value === 'canvas') {
      if (drawCursorSize.value > (0.001 * imageDimensions.value.width)) {
        drawCursorSize.value /= 1.3;
      }
    }
  },
});

watch(drawMode, () => {
  if (drawMode.value !== "line") {
    nextTick(() => { doneVertexDraw.value = true; });
  } else {
    nextTick(() => { doneVertexDraw.value = false; });
  }
});

const imageAuditHotkeys = ref({
  'ArrowRight': async () => {
    if (!isPropagating.value) {
      await skipTaskImage();
    }
  },
});
const sequenceControlHotkeys = ref({
  'ArrowLeft': async () => {
    if (!isPropagating.value) {
      await animationControls.value.handleSkipPrevious();
    }
  },
  'ArrowRight': async () => {
    if (!isPropagating.value) {
      await animationControls.value.handleSkipNext();
    }
  },
});
const taskImageAuditHotkeys = computed(() => ({
  'Delete': async () => {
    if (selectedAnnotationIdentifiers.value.length > 0) {
      const annotationsToDelete = selectedAnnotationIdentifiers.value.map((identifier) => annotationIdentifierMap.value[identifier]);
      setCurrentAnnotations([...currentSample.value.annotations.filter((anno) => !annotationsToDelete.includes(anno))]);
      selectedAnnotationIdentifiers.value = [];
    }
  },
  'ctrl,c': () => {
    handleCopyAnnotations();
  },
  'ctrl,v': () => {
    handlePasteAnnotations();
  },
  'ctrl,x': () => {
    handleDeleteAnnotationBulk();
  },
  ...viewHotkeys,
  ...auditHotkeys.value,
  ...(internalEditAsSequence.value || isSequence.value ? sequenceControlHotkeys.value : imageAuditHotkeys.value),
  ...toolHotkeys.value,
  ...drawToolHotKeys.value,
}));

const imageEditorHotkeys = computed(() => ({
  'Delete': async () => {
    if (selectedAnnotationIdentifiers.value.length > 0) {
      const annotationsToDelete = selectedAnnotationIdentifiers.value.map((identifier) => annotationIdentifierMap.value[identifier]);
      setCurrentAnnotations([...currentSample.value.annotations.filter((anno) => !annotationsToDelete.includes(anno))]);
      selectedAnnotationIdentifiers.value = [];
    }
  },
  'ctrl,c': () => {
    handleCopyAnnotations();
  },
  'ctrl,v': () => {
    handlePasteAnnotations();
  },
  'ctrl,x': () => {
    handleDeleteAnnotationBulk();
  },
  'Enter': async () => {
    if (annotationToolSequenceRibbon.value) {
      await annotationToolSequenceRibbon.value.handleLabellingSubmit();
    } else if (annotationToolAGTGRibbon.value) {
      await annotationToolAGTGRibbon.value.handleSaveSAM2Annotations();
    }
  },
  ...viewHotkeys,
  ...(internalEditAsSequence.value || isSequence.value ? sequenceControlHotkeys.value : {}),
  ...toolHotkeys.value,
  ...drawToolHotKeys.value,
}));

const imageViewerHotkeys = computed(() => ({
  ...viewHotkeys,
  ...(internalEditAsSequence.value || isSequence.value ? sequenceControlHotkeys.value : {}),
  ...toolHotkeys.value,
}));

const {
  areListenersActive: areAuditListenersActive,
  activateListeners: activateImageAuditListeners,
  deactivateListeners: deactivateImageAuditListeners,
} = useAnnotationEditorModalHotkeys(taskImageAuditHotkeys);
const {
  areListenersActive: areEditorListenersActive,
  activateListeners: activateImageEditorListeners,
  deactivateListeners: deactivateImageEditorListeners,
} = useAnnotationEditorModalHotkeys(imageEditorHotkeys);
const {
  areListenersActive: areViewerListenersActive,
  activateListeners: activateImageViewerListeners,
  deactivateListeners: deactivateImageViewerListeners,
} = useAnnotationEditorModalHotkeys(imageViewerHotkeys);
watch(auditorType, (auditorTypeVal) => {
  if (auditorTypeVal !== 'image') {
    deactivateImageAuditListeners();
  } else {
    if (!areAuditListenersActive.value) {
      activateImageAuditListeners();
    }
  }

  // Toggle showing the visualizations
  if (auditorTypeVal === 'annotation') {
    showLayouts.value = false;
  } else {
    nextTick(() => {
      showLayouts.value = true;
    });
  }
});
watch(mode, (modeVal) => {
  if (modeVal === 'edit' && auditorType.value === 'image') {
    nextTick(() => { prevInternalAnnotations.value = null; });
    if (areViewerListenersActive.value) {
      deactivateImageViewerListeners();
    }
    activateImageEditorListeners();
  } else {
    handleRevertChanges();
    if (areEditorListenersActive.value) {
      deactivateImageEditorListeners();
    }
    activateImageViewerListeners();
  }
});

if (reviewSettings.value?.initialAuditorType === 'image' || route.query.auditorType === 'image') {
  activateImageAuditListeners();
} else if (mode.value === 'edit') {
  activateImageEditorListeners();
} else if (mode.value === 'view') {
  activateImageViewerListeners();
}

// Zoom
function handleZoomToFit() {
  annotationCanvas.value.zoomToFitImage();
}
function handleZoomToSelection() {
  annotationCanvas.value.zoomToSelection();
}
function handleZKey() {
  if (selectedAnnotationIdentifiers.value.length > 0) {
    annotationCanvas.value.zoomToSelection();
  } else {
    annotationCanvas.value.zoomToFitImage();
  }
}

function filterAnnotationsBySet(annotations, sets) {
  if (sets) {
    // should return all those with the current annotation set and those without
    // the ones without annotation set are recently created
    return annotations.filter((anno) => sets.includes(anno.annotation_set_id) || !anno.annotation_set_id);
  }
  return annotations;
}

// Computed
const colorBy = computed(() => annotationDisplaySettings.value?.colorAnnotationsBy?.type);

const groupedAnnotations = computed(() => {
  if ((internalEditAsSequence.value || isSequence.value) && currentSample.value) {
    // For animation
    const groupedData = {};
    topics.value.forEach((key) => {
      groupedData[key] = [];
    });

    const totalAnnotations = [...currentSample.value.annotations, ...Object.values(pendingAnnotations.value).flat()];
    const filteredAnnotations = filterAnnotationsBySet(totalAnnotations, annotationDisplaySettings.value.displayAnnotationSets.annotation_sets);
    filteredAnnotations.forEach((item) => {
      const itemType = item.type;
      if (!groupedData[itemType]) {
        groupedData[itemType] = [];
      }
      groupedData[itemType].push(item);
    });
    return groupedData;
  } else {
    // For non-animation
    if (!currentSample.value.annotations) return {};
    const groupedData = {};
    const filteredAnnotations = filterAnnotationsBySet(currentSample.value.annotations, annotationDisplaySettings.value.displayAnnotationSets.annotation_sets);
    filteredAnnotations.forEach((item) => {
      const itemType = item.type;
      if (!groupedData[itemType]) {
        groupedData[itemType] = [];
      }
      groupedData[itemType].push(item);
    });
    return groupedData;
  }
});

// Watchers

// watch(() => currentSample.value.imageObj, async () => {
//   setCurrentAnnotations(currentSample.value?.imageObj?.annotations);
//   nextTick(() => { prevInternalAnnotations.value = null; });

//   if (isSequence.value) {
//     topics.value = await getTopics();
//   }
// });

watch(() => currentSample.value.annotations, (newAnnotations, prevAnnotations) => {
  prevInternalAnnotations.value = JSON.parse(JSON.stringify(prevAnnotations || []));

  if (reviewSettings.value?.reviewTask?.type === 'audit' && doneImageOpen.value) {
    newAnnotations.forEach((anno) => {
      anno.reviewStatus = 'verified';
    });
  }
}, { deep: true });

watch(annotationSetToEdit, () => {
  if (annotationSetToEdit.value) {
    annotationDisplaySettings.value.displayAnnotationSets.annotation_sets = [annotationSetToEdit.value.id];
  }
});

watch(imageDimensions, () => {
  if (imageDimensions.value) {
    drawCursorSize.value = 0.04 * imageDimensions.value.width;
  }
});

watch(showTypes, () => {
  if (showTypes.value.pcd || showTypes.value['3dbox'] || showTypes.value.lidar) {
    handleAddVisualization("3d-canvas");
  }
  if (showTypes.value.depthmap) {
    handleAddVisualization("depth-map");
  }
}, { deep: true });

async function handleRouteParams() {
  if (route.query.annotationDisplaySettings) {
    annotationDisplaySettings.value = JSON.parse(decodeURI(route.query.annotationDisplaySettings));
  }
  if (route.query.showTypes) {
    showTypes.value = JSON.parse(decodeURI(route.query.showTypes));
  }
  // if (this.$route.query.imagesParams) {
  //   this.setImageParams(JSON.parse(decodeURI(this.$route.query.imagesParams)));
  // }
  if (route.query.datasetFilters) {
    setDatasetFilters(JSON.parse(decodeURI(route.query.datasetFilters)));
  }

  if (route.query.reviewTaskId) {
    reviewSettings.value=null;

    const reviewTask = await getReviewTask(parseInt(route.query.reviewTaskId));
  
    const reviewSession = await createReviewSession(parseInt(route.query.reviewTaskId));

    reviewSettings.value = {
      reviewSessionMethod: 'review_annotation_editor',
      batchSize: 10,
      samplingMethod: 'random',
      reviewSession: reviewSession,
      reviewTask,
      initialAuditorType: auditorType.value,
    };

  
  }
}

async function getDatasetInfo(id) {
  const dataConnect = new DatastoreConnect();
  const resp = await dataConnect.getDatasetById({
    dataset_id: id,
  })
    .catch((error) => {
      $toast.open({
        queue: true,
        message: `Failed to retrieve dataset: ${error}`,
        type: 'error',
        position: 'top',
        dismissible: true,
      });
    });
  if (!resp || resp.error || !resp.result) {
    $toast.open({
      queue: true,
      message: `Failed to retrieve dataset: ${resp.error.message}`,
      type: 'error',
      position: 'top',
      dismissible: true,
    });
    return null;
  }
  return resp.result;
}

async function createAnnotationSet(name, dataset_id) {
  const dataConnect = new DatastoreConnect();
  const resp = await dataConnect.createAnnotationSet({
    name,
    description: "",
    operator: store.state.user.user.username,
    origin_x: 0,
    origin_y: 0,
    dataset_id,
  })
    .catch((error) => {
      $toast.open({
        queue: true,
        message: `Failed to create annotation set: ${error}`,
        type: 'error',
        position: 'top',
        dismissible: true,
      });
    });
  if (resp.error || !resp.result) {
    $toast.open({
      queue: true,
      message: `Failed to create annotation set: ${resp.error.message}`,
      type: 'error',
      position: 'top',
      dismissible: true,
    });
  }
}

onMounted(async () => {
  if (!canEdit.value) {
    mode.value = 'view';
  }

  await handleRouteParams();

  if (reviewSettings.value?.reviewTask?.id) {
    // Setup for review task
    if (hasSourceAnnotationSet.value) {
      annotationSetToEdit.value =   sourceAnnotationSet.value;
    } else {
      annotationSetToEdit.value =   destinationAnnotationSet.value;
    }

    // Get all sequences for the dataset
    if (hasReviewTask.value) {
      sequences.value = await getTaskSequences();
    }

    // Setup image and annotations
    refreshAnnotationSets(reviewSettings.value.reviewTask.dataset_id)
      .then(() => {
        if (annotationSets.value.length === 0) {
          $toast.open({
            queue: true,
            message: "An annotation set is required to start editing. Create annotation sets from sidebar to edit.",
            type: 'error',
            position: 'top',
            dismissible: true,
          });
        }
      });
    refreshLabels(reviewSettings.value.reviewTask.dataset_id)
      .then(() => {
        annotationDisplaySettings.value.displayLabels.labels = [...labels.value.map((l) => l.index)];
        if (labels.value.length > 0) {
          selectedLabel.value = labels.value[0];
        }
        if (labels.value.length === 0) {
          $toast.open({
            queue: true,
            message: "Labels are required for editing annotations. Create labels from sidebar to start.",
            type: 'error',
            position: 'top',
            dismissible: true,
          });
        }
      });
    await setupTaskImages();
  } else {
    // Setup for gallery viewer/editor
    internalEditAsSequence.value = true;
    selectedLabel.value = labels.value[0];

    const datastore = new DatastoreConnect();
    const params = {
      id: parseInt(route.query.image_id),
      get_annotations: true,
    };
    const imageResult = await datastore.getImage(params)
      .then((resp) => {
        if (resp.error !== undefined) {
          throw Error(resp.error.message);
        }
        return resp.result;
      })
      .catch((error) => {
        $toast.open({
          queue: true,
          message: `Failed to get image: ${error.message}`,
          type: 'error',
          position: 'top',
          dismissible: true,
        });
      });

    currentSample.value = {
      image: null,
      imageObj: imageResult,
      annotations: copyAnnotationsObject(imageResult.annotations),
      loaded: false,
    };

    await Promise.all([
      refreshAnnotationSets(imageResult.dataset_id)
        .then(() => {
          if (annotationSets.value.length === 0) {
            createAnnotationSet("annotations", imageResult.dataset_id)
              .then(() => {
                refreshAnnotationSets(imageResult.dataset_id);
              });
          }
          if (annotationSets.value.length > 0 && annotationDisplaySettings.value.displayAnnotationSets.annotation_sets.length === 0) {
            annotationDisplaySettings.value.displayAnnotationSets.annotation_sets = [annotationSets.value[0].id];
          }
        }),
      refreshLabels(imageResult.dataset_id)
        .then(() => {
          if (labels.value.length > 0) {
            selectedLabel.value = labels.value[0];
          }
          if (labels.value.length === 0) {
            $toast.open({
              queue: true,
              message: "Labels are required for editing annotations. Create labels from sidebar to start.",
              type: 'info',
              position: 'top',
            });
          }
        }),
    ]);

    if (canEdit.value) {
      handleStartEditingAnnotations(annotationSets.value[0]);
    }
  }

  isMounted.value = true;
});

// Methods
async function createNewInternalAnnotation(annotation) {
  if (isAuditMode.value) {
    annotation.reviewStatus = 'verified';
  }
  annotation.annotation_set_id = destinationAnnotationSet.value.id;
  addToCurrentAnnotations([annotation]);
}

function handleTaskImageClicked(imageIndex) {
  switchToTaskImage(imageIndex);
}

function handleAnnotationBulkDeleteComplete() {
  animationControls.value.handleClearCache();
}

function handleDeleteAnnotationBulk() {
  bulkDeleteModal.value.showModal();
}

async function handleAnnotationReviewItemEdit(annotation) {
  const annotationIdentifier = Object.keys(annotationIdentifierMap.value).find((key) => annotationIdentifierMap.value[key] === annotation);
  selectedAnnotationIdentifiers.value = [annotationIdentifier];
  await nextTick();
  annotationCanvas.value.zoomToSelection();
}

async function handleSubmit() {
  // Editing through labelling task
  await handleLabellingSubmitAnnotations();
}

async function handleSequenceSubmit() {
  // Go to next frame
  if (frame.value + 1 <= Object.keys(animationImageCache.value).length) {
    await animationControls.value.handleSkipNext();
  }
}

function handleHoveredAnnotation(identifier) {
  hoveredAnnotationListIdentifier.value = identifier;
}

function handlePaneClick(event, pane) {
  activePane.value = pane;

  if (pane === 'canvas') {
    toolHotkeys.value = canvasHotkeys;
  } else if (pane === '3d-canvas') {
    toolHotkeys.value = canvas3DHotkeys;
  }

  if (pane === '3d-canvas' && current3DCanvasTool.value === '3d-box') {
    event.stopPropagation();
  }
}

function handleSelectedSequenceChanged(selectedSequence) {
  currentTaskImage.value = selectedSequence;
}

// Visualizations
const animationControls = ref(null);
const {
  keyframeAnnotations,
  topics,
  isInitializingAnimation,
  frame,
  frameCount,
  animationImageCache,
} = storeToRefs(useViewerVisualizationsStore());
const showLayouts = ref(true);
if (auditorType.value === 'annotation') {
  showLayouts.value = false;
}
onUnmounted(() => {
  const { $reset } = useViewerVisualizationsStore();
  $reset();
});
watch(currentSample, () => {
  // if (currentSample.value) {
  //   setCurrentAnnotations([
  //     ...copyAnnotationsObject(currentSample.value.imageObj?.annotations),
  //     ...keyframeAnnotations.value,
  //   ]);
  // }
});
const isLoadingSample = computed(() => {
  if (currentSample.value) {
    return !currentSample.value.loaded;
  } else if (internalEditAsSequence.value || isSequence.value) {
    return true;
  }
  return false;
});
watch(() => currentSample.value.imageObj, () => {
  nextTick(() => {
    selectedAnnotationIdentifiers.value = [];
    if (hasSourceAnnotationSet.value) {
      if (currentSample.value?.imageObj?.review_status === "Done") {
        annotationSetToEdit.value = destinationAnnotationSet.value;
      } else {
        annotationSetToEdit.value = sourceAnnotationSet.value;
      }
    } else {
      annotationSetToEdit.value = destinationAnnotationSet.value;
    }
  });
});

// Setup all topics in sequence
async function getTopics() {
  const dataConnect = new DatastoreConnect();
  const topicsResp = await dataConnect.getDatasetTopics({ dataset_id: currentSample.value?.imageObj?.dataset_id });
  if (!topicsResp.result || topicsResp.error) {
    return null;
  }
  return topicsResp.result;
}

function handleAnnotationDeleteClicked(identifier) {
  setCurrentAnnotations([...currentSample.value.annotations.filter((anno) => anno.id !== identifier)]);
  sam2Annotations.value = [...sam2Annotations.value.filter((anno) => anno.id !== identifier)];
  aigt3dAnnotations.value = [...aigt3dAnnotations.value.filter((anno) => anno.id !== identifier)];
  aigt2dAnnotations.value = [...aigt2dAnnotations.value.filter((anno) => anno.id !== identifier)];
  selectedAnnotationIdentifiers.value = [];
}

const {
  handleAddVisualization,
  handleVisualizationSelected,
  handleVisualizationContainerDrop,
  handleLayoutSelected,
  visualizationLocations,
  getLocationId,
  layout,
} = useVisualizationDisplays();

async function downloadImage() {
  const url = `/v3/enterprise/image/${currentSample.value.imageObj.id}`;

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = currentSample.value.imageObj.name || currentSample.value.imageObj.id || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Error downloading the image:', error);
  }
}

const pendingAnnotations = computed(() => ({
  'seg': [...sam2Annotations.value].filter((anno) => anno.image_id === currentSample.value?.imageObj?.id),
  '3dbox': [...aigt3dAnnotations.value].filter((anno) => anno.image_id === currentSample.value?.imageObj?.id),
  '2dbox': [...aigt2dAnnotations.value].filter((anno) => anno.image_id === currentSample.value?.imageObj?.id),
}));

async function refreshAnnotationSets(dataset_id) {
  annotationSets.value = await getAnnotationSetsForDataset(dataset_id);
  annotationSets.value = [...annotationSets.value];
}

async function refreshLabels(dataset_id) {
  const refreshedLabels = await getLabelListForDataset(dataset_id);
  labels.value = refreshedLabels;
  labelColorMap.value = useAnnotationColorMap({ items: ref(refreshedLabels), key: 'index' });

  setDatasetLabels(refreshedLabels);

  return refreshedLabels;
}

async function getAnnotationSetsForDataset(dataset_id) {
  const dataConnect = new DatastoreConnect();
  const resp = await dataConnect.getAnnotationSets({
    dataset_id,
    get_num_annotations: true,
    get_num_images: true,
    get_labels: true,
  })
    .catch((error) => {
      console.log('Failed to retrieve sets:', error);
    });
  if (!resp || resp.error || !resp.result) {
    return [];
  }
  return resp.result;
}

async function getLabelListForDataset(dataset_id) {
  const dataConnect = new DatastoreConnect();
  const resp = await dataConnect.getLabelList({ dataset_id })
    .catch((error) => {
      console.log('Failed to retrieve labels:', error);
    });
  if (!resp || resp.error || !resp.result) {
    return [];
  }
  return resp.result;
}

async function handleStartEditingAnnotations(annotationSet) {
  annotationSetToEdit.value = annotationSet;
  reviewSettings.value = {
    reviewSessionMethod: "review_annotation_editor",
    batchSize: 10,
    samplingMethod: "random",
    reviewSession: null,
    reviewTask: {
      id: null,
      type: "audit",
      created_at: null,
      source_annotation_set_id: annotationSet.id,
      dest_annotation_set_id: annotationSet.id,
      image_id: parseInt(route.query.image_id),
      dataset_id: annotationSet.dataset_id,
      name: "",
      description: "",
      permission_type: "default",
      organization_id: 5,
      remainingCount: null,
    },
    initialAuditorType: "image",
  };
  selectedLabel.value = labels.value[0];
  mode.value = 'edit';
}

const combined = computed(() => ({
  imageObj: currentSample.value.imageObj,
  annotations: currentSample.value.annotations,
  showTypes: showTypes.value,
  // imagesParams: imagesParams.value,
  datasetFilters: datasetFilters.value,
  annotationDisplaySettings: annotationDisplaySettings.value,
}));

watch(combined, () => {
  if (route.path === "/datasets/gallery/viewer") {
    router.replace({
      path: route.path,
      query: {
        image_id: encodeURI(JSON.stringify(currentSample.value?.imageObj?.id)),
        showTypes: encodeURI(JSON.stringify(showTypes.value)),
        // imagesParams: encodeURI(JSON.stringify(this.imagesParams)),
        datasetFilters: encodeURI(JSON.stringify(datasetFilters.value)),
        annotationDisplaySettings: encodeURI(JSON.stringify(annotationDisplaySettings.value)),
      },
    });
  }
}, { deep: true });

async function getReviewTask(reviewTaskId) {
  // this.loading = true;
  const dataConnect = new DatastoreConnect();
  const resp = await dataConnect.getReviewTaskById({ review_task_id: reviewTaskId })
    .catch((error) => {
      // TODO: handle error
      // this.loading = false;
      console.log(error);
    });
  if (resp.error || !resp.result) {
    // TODO: handle error
    // this.loading = false;
    return null;
  }
  // this.loading = false;
  return resp.result;
}

async function createReviewSession(reviewTaskId) {
  const dataConnect = new DatastoreConnect();
  const reviewSession = await dataConnect.addReviewSession({
    reviewer: store.state.user.user.username,
    duration: 0,
    review_task_id: reviewTaskId,
  })
    .then((resp) => {
      if (resp.error !== undefined) {
        throw Error(resp.error);
      }
      return resp;
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });

  return reviewSession;
}

function openAddAnnotationSetsModal() {
  createAnnotationSetModal.value.showModal();
}

async function newAnnotationSetCreated() {
  if (sourceDatasetId.value) {
    // Setup image and annotations
    refreshAnnotationSets(sourceDatasetId.value)
      .then(() => {
        annotationDisplaySettings.value.displayAnnotationSets.annotation_sets = [annotationSets.value[0].id];
      });
  }
}

function openAddLabelsModal() {
  createLabelsModal.value.showModal();
}

async function newLabelsCreated() {
  if (sourceDatasetId.value) {
    // Setup image and annotations
    refreshLabels(sourceDatasetId.value)
      .then(() => {
        annotationDisplaySettings.value.displayLabels.labels = [...labels.value.map((l) => l.index)];
        if (labels.value.length > 0) {
          selectedLabel.value = labels.value[0];
        }
      });
  }
}

function setDatasetLabels(newLabels) {
  const currentDataset = store.state.datasets.currentDataset;
  const updatedDataset = {
    ...currentDataset,
    labels: newLabels,
  };
  store.commit('datasets/setCurrentDataset', updatedDataset);
  store.commit('datasets/updateDataset', updatedDataset);
}

async function updateLabelColor(params, obj) {
  params.dataset_id = obj.dataset_id;
  const dataConnect = new DatastoreConnect();
  await dataConnect.updateLabel(params)
    .then(async (data) => {
      if (!data.error) {
        await refreshLabels(obj.dataset_id);
      }
    })
    .catch((e) => console.log(e));
}

async function handleImageGroupChanged(group_id) {
  const oldGroupId = imageGroupId.value;
  currentSample.value.imageObj.group_id = group_id;

  const dataConnect = new DatastoreConnect();
  await dataConnect.setImageGroupId({ image_id: currentSample.value.imageObj.id, group_id })
    .then(async (data) => {
      if (data.error) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: `Failed to set group: ${data.error.message}`,
          type: 'error',
          position: 'top',
        });
        // Revert group_id due to error
        currentSample.value.imageObj.group_id = oldGroupId;
      }
    });
}
</script>

<style lang="scss" scoped>
.display {
  display: flex;
  flex-direction: row;
  position: relative;
  width: 100%;
  height: 100%;
  outline: none;
  overflow: hidden;
}

.visualization {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  position: relative;
  min-width: 0;

  &__horizontal-divider {
    outline: solid 2px gray;
    z-index: 2;
  }

  &__vertical-divider {
    min-width: 3px;
    max-width: 3px;
    background: var(--color-primary-400);
    z-index: 2;
  }
}

.annotation-editor-modal {
  &__sidebar {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 300px;
    min-width: 300px;
    padding: 0;
    z-index: 2;
    @include themify() {
      background: themed('color-white-100');
    }

    h3 {
      display: flex;
      align-items: center;
      justify-content: space-between;
      min-height: 40px;
      padding: 4px 8px;
      margin: 0;
      text-align: left;
      box-shadow: 0px 1px 3px grey;
      @include themify() {
        background: themed('color-white-900');
      }
    }
  }
}

.annotation-tool {
  display: flex;
  position: relative;
  align-items: center;
  justify-content: center;
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
  @include themify() {
    background: themed('image-background-fill');
  }
}

.annotation-review-sidebar {
  @include themify() {
    background: themed('color-white-100');
  }

  &__buttons {
    width: 100%;
    padding: 0 16px;

    button {
      width: 100%;
    }
  }

  &__annotations {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 300px;
    height: 100%;
    overflow-y: auto;
    scroll-snap-type: y mandatory;
  }

  &__snap-container {
    scroll-snap-align: center;
    min-height: calc(100% / 3);
    align-items: center;
    display: flex;
    flex-direction: column;
    width: 100%;
    justify-content: center;
    padding: 16px;

    &._active {
      background: rgb(0,0,0,0.125);
    }
  }
}

#no-image-remaining {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--color-primary);
}

.image-group {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 8px;

  &__label {
    color: var(--body-text-color-secondary);
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    margin-bottom: 2px;
  }
}

:deep(.v-expansion-panel-text__wrapper) {
  padding: 8px 12px 16px;
}
:deep(.v-expansion-panel-title__overlay) {
  pointer-events: none;
}

</style>
